import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Calendar, MapPin, Users, X, CheckCircle2, Search, LayoutGrid, List, Layers,
  Clock, Lock, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["All", "Conference", "Workshop", "MAD Week", "Webinar", "Field Trip"];
const DIETARY = ["None", "Vegetarian", "Vegan", "Kosher", "Halal", "Gluten-Free"];

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-ZA", {
      day: "numeric", month: "long", year: "numeric",
    });
  } catch { return iso; }
}

function StatusPill({ event }) {
  const isOpen = event.is_open_for_reservation;
  const label = event.is_past
    ? "Closed · Past"
    : event.status === "closed"
    ? "Closed"
    : event.reservations_count >= event.capacity
    ? "Closed · Full"
    : "Open";
  return (
    <span
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wider uppercase"
      style={{
        backgroundColor: isOpen ? "rgba(39,68,40,0.12)" : "rgba(201,122,62,0.14)",
        color: isOpen ? "var(--ser-primary)" : "var(--ser-clay-dark)",
      }}
      data-testid={`event-status-${event.event_id}`}
    >
      {isOpen ? <Clock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
      {label}
    </span>
  );
}

export default function EventsPage({
  forcedCategory = null,
  pageEyebrow = "Events & Webinars",
  pageTitle = "SER Africa Events",
  pageSubtitle = "Join conferences, workshops, webinars, and field trips across the continent.",
  testIdRoot = "events-page",
}) {
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState(forcedCategory || "All");
  const [query, setQuery] = useState("");
  const [view, setView] = useState("grid"); // grid | list
  const [grouped, setGrouped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({
    name: "", email: "", cell_number: "",
    dietary_requirements: "None", comments: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const nav = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/events").then(({ data }) => setEvents(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        name: f.name || user.name || "",
        email: f.email || user.email || "",
        cell_number: f.cell_number || user.cell_number || "",
      }));
    }
  }, [user]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const activeFilter = forcedCategory || filter;
    return events
      .filter((e) => activeFilter === "All" || e.category === activeFilter)
      .filter((e) =>
        !q ||
        e.title.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q) ||
        e.location.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q)
      );
  }, [events, filter, query, forcedCategory]);

  const groups = useMemo(() => {
    if (!grouped) return null;
    const byCat = {};
    for (const e of filtered) {
      if (!byCat[e.category]) byCat[e.category] = [];
      byCat[e.category].push(e);
    }
    // preserve category order
    return CATEGORIES.slice(1).filter((c) => byCat[c]).map((c) => ({ cat: c, items: byCat[c] }));
  }, [filtered, grouped]);

  const openReserve = (event) => {
    if (!event.is_open_for_reservation) return;
    // The Africa Ecosystem Restoration Symposium (branded as the "SER Africa
    // Annual Conference") has its own dedicated registration page — route
    // there instead of opening the generic reservation modal.
    // We match either an explicit "symposium" in the title OR any event in the
    // Conference category (only one Conference exists on the site: the
    // annual symposium).
    const title = (event.title || "").toLowerCase();
    const isSymposium =
      title.includes("symposium") ||
      title.includes("annual conference") ||
      title.includes("ecosystem restoration") ||
      event.category === "Conference";
    if (isSymposium) {
      nav("/conferences/symposium-2026");
      return;
    }
    if (!isAuthenticated) {
      toast.info("Please sign in to reserve your spot.");
      nav("/login", { state: { from: "/events" } });
      return;
    }
    setModal(event);
  };

  const submitReservation = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/events/${modal.event_id}/reservations`, form);
      toast.success("Reservation confirmed! See it in your dashboard.");
      setModal(null);
      setForm((f) => ({ ...f, comments: "", dietary_requirements: "None" }));
      load();
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid={testIdRoot}>
      <section
        className="ser-section text-center"
        style={{
          background: "linear-gradient(160deg, var(--ser-primary-dark) 0%, var(--ser-primary) 70%)",
          color: "white",
        }}
      >
        <div className="ser-container max-w-4xl">
          <p className="eyebrow mb-4" style={{ color: "#fff", opacity: 0.85 }}>{pageEyebrow}</p>
          <h1 className="font-serif-display text-5xl md:text-7xl mb-5" style={{ fontWeight: 300 }}>{pageTitle}</h1>
          <p className="text-lg" style={{ color: "rgba(255,255,255,0.85)" }}>
            {pageSubtitle}
          </p>
        </div>
      </section>

      <section className="ser-section">
        <div className="ser-container">
          {/* Webinar callout */}
          <Link
            to="/events/webinars"
            className="ser-card group flex items-center gap-4 p-5 mb-8 hover:shadow-lg transition-shadow"
            style={{ borderLeft: "4px solid var(--ser-primary)" }}
            data-testid="events-webinars-callout"
          >
            <div
              className="w-12 h-12 rounded-full flex-shrink-0 flex items-center justify-center"
              style={{ backgroundColor: "var(--ser-sand)" }}
            >
              <PlayCircle className="w-6 h-6" style={{ color: "var(--ser-primary)" }} />
            </div>
            <div className="flex-1 min-w-0">
              <p
                className="text-[11px] font-bold uppercase tracking-wider mb-1"
                style={{ color: "var(--ser-clay)" }}
              >
                Video library
              </p>
              <p className="text-sm md:text-base font-bold" style={{ color: "var(--ser-teal)" }}>
                Watch our webinar recordings
              </p>
              <p className="text-xs mt-0.5" style={{ color: "var(--ser-text-muted)" }}>
                Pre-Symposium Webinar, Strategic Plan launch and past recordings.
              </p>
            </div>
            <span
              className="text-xs font-bold uppercase tracking-wider hidden sm:block"
              style={{ color: "var(--ser-primary)" }}
            >
              Open →
            </span>
          </Link>

          {/* Toolbar: search + view toggles */}
          <div className="flex flex-col lg:flex-row gap-4 mb-8" data-testid="events-toolbar">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: "var(--ser-text-muted)" }} />
              <input
                type="search"
                placeholder="Search events by title, description, location, or category…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="ser-input"
                style={{ paddingLeft: "2.75rem" }}
                data-testid="events-search-input"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setGrouped(!grouped)}
                className="btn-ser-ghost"
                style={{ backgroundColor: grouped ? "var(--ser-sand)" : "transparent" }}
                data-testid="toggle-group-btn"
              >
                <Layers className="w-4 h-4" /> {grouped ? "Ungroup" : "Group by type"}
              </button>
              <div className="flex rounded-full overflow-hidden" style={{ backgroundColor: "var(--ser-sand)" }}>
                <button
                  onClick={() => setView("grid")}
                  className="px-4 py-2 flex items-center gap-1.5 text-sm"
                  style={{
                    backgroundColor: view === "grid" ? "var(--ser-primary)" : "transparent",
                    color: view === "grid" ? "white" : "var(--ser-text-muted)",
                  }}
                  data-testid="view-grid-btn"
                >
                  <LayoutGrid className="w-4 h-4" /> Grid
                </button>
                <button
                  onClick={() => setView("list")}
                  className="px-4 py-2 flex items-center gap-1.5 text-sm"
                  style={{
                    backgroundColor: view === "list" ? "var(--ser-primary)" : "transparent",
                    color: view === "list" ? "white" : "var(--ser-text-muted)",
                  }}
                  data-testid="view-list-btn"
                >
                  <List className="w-4 h-4" /> List
                </button>
              </div>
            </div>
          </div>

          {/* Category tabs (hidden when category is forced) */}
          <div className={`flex flex-wrap gap-2 mb-12 ${forcedCategory ? "hidden" : ""}`} data-testid="event-filter-tabs">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className="px-5 py-2.5 rounded-full text-sm font-medium transition-all"
                style={{
                  backgroundColor: filter === c ? "var(--ser-primary)" : "var(--ser-sand)",
                  color: filter === c ? "white" : "var(--ser-text-muted)",
                }}
                data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {c}
              </button>
            ))}
          </div>

          {loading ? (
            <p className="text-center" style={{ color: "var(--ser-text-muted)" }}>Loading events…</p>
          ) : filtered.length === 0 ? (
            <p className="text-center py-16" style={{ color: "var(--ser-text-muted)" }} data-testid="no-events-msg">
              No events match your search.
            </p>
          ) : grouped ? (
            <div className="space-y-12">
              {groups.map((g) => (
                <div key={g.cat} data-testid={`group-${g.cat.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="flex items-center gap-3 mb-6">
                    <h2 className="font-serif-display text-3xl" style={{ color: "var(--ser-primary-dark)" }}>
                      {g.cat}
                    </h2>
                    <span className="ser-tag">{g.items.length}</span>
                  </div>
                  {view === "grid" ? <GridView events={g.items} onReserve={openReserve} /> : <ListView events={g.items} onReserve={openReserve} />}
                </div>
              ))}
            </div>
          ) : view === "grid" ? (
            <GridView events={filtered} onReserve={openReserve} />
          ) : (
            <ListView events={filtered} onReserve={openReserve} />
          )}
        </div>
      </section>

      {/* Reservation Modal */}
      {modal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
          style={{ backgroundColor: "rgba(25,42,25,0.75)" }}
          data-testid="reservation-modal"
          onClick={() => setModal(null)}
        >
          <div
            className="relative max-w-lg w-full rounded-2xl my-auto"
            style={{ backgroundColor: "var(--ser-surface)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setModal(null)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--ser-sand)] z-10"
              data-testid="close-modal-btn"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="p-8">
              <p className="eyebrow mb-2">Reserve your spot</p>
              <h3 className="font-serif-display text-3xl mb-1" style={{ color: "var(--ser-primary-dark)" }}>
                {modal.title}
              </h3>
              <div className="flex gap-4 text-xs mb-6 mt-2" style={{ color: "var(--ser-text-muted)" }}>
                <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(modal.date)}</span>
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {modal.location}</span>
              </div>

              <form onSubmit={submitReservation} className="space-y-4" data-testid="reservation-form">
                <div>
                  <label className="ser-label">Full name</label>
                  <input required value={form.name}
                    onChange={(ev) => setForm({ ...form, name: ev.target.value })}
                    className="ser-input" data-testid="reservation-name" />
                </div>
                <div>
                  <label className="ser-label">Email</label>
                  <input type="email" required value={form.email}
                    onChange={(ev) => setForm({ ...form, email: ev.target.value })}
                    className="ser-input" data-testid="reservation-email" />
                </div>
                <div>
                  <label className="ser-label">Cell number</label>
                  <input required value={form.cell_number}
                    onChange={(ev) => setForm({ ...form, cell_number: ev.target.value })}
                    className="ser-input" data-testid="reservation-cell" />
                </div>
                <div>
                  <label className="ser-label">Dietary requirements</label>
                  <select
                    className="ser-input"
                    value={form.dietary_requirements}
                    onChange={(ev) => setForm({ ...form, dietary_requirements: ev.target.value })}
                    data-testid="reservation-dietary"
                  >
                    {DIETARY.map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div>
                  <label className="ser-label">
                    Comments <span className="text-xs font-normal" style={{ color: "var(--ser-text-muted)" }}>(optional)</span>
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Anything we should know? Accessibility needs, travel info, questions…"
                    className="ser-input"
                    value={form.comments}
                    onChange={(ev) => setForm({ ...form, comments: ev.target.value })}
                    data-testid="reservation-comments"
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-ser-accent w-full mt-2"
                  data-testid="submit-reservation-btn"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  {submitting ? "Reserving…" : "Confirm reservation"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const isSymposiumEvent = (e) => {
  const t = (e.title || "").toLowerCase();
  return (
    t.includes("symposium") ||
    t.includes("annual conference") ||
    t.includes("ecosystem restoration") ||
    e.category === "Conference"
  );
};

function FeaturedRow({ event, onReserve }) {
  return (
    <article
      className="ser-card flex flex-col md:flex-row mb-8 overflow-hidden"
      style={{ borderLeft: "4px solid var(--ser-clay)" }}
      data-testid={`event-card-${event.event_id}`}
    >
      <div className="md:w-3/5 relative" style={{ backgroundColor: "var(--ser-primary-dark)" }}>
        <div className="aspect-[3/2] md:aspect-auto md:h-full">
          <img
            src={event.image_url}
            alt={event.title}
            className="w-full h-full object-contain"
          />
        </div>
        <span
          className="absolute top-4 left-4 text-[0.62rem] font-bold uppercase tracking-widest px-2 py-1"
          style={{ backgroundColor: "var(--ser-clay)", color: "white" }}
        >
          Featured event
        </span>
      </div>
      <div className="md:w-2/5 p-6 md:p-8 flex flex-col">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="ser-tag">{event.category}</span>
          <StatusPill event={event} />
        </div>
        <h3
          className="font-serif-display text-2xl md:text-3xl mb-3 leading-snug"
          style={{ color: "var(--ser-primary-dark)" }}
        >
          {event.title}
        </h3>
        <p className="text-sm mb-5" style={{ color: "var(--ser-text-muted)" }}>
          {event.description}
        </p>
        <div className="space-y-2 text-xs mb-6" style={{ color: "var(--ser-text-muted)" }}>
          <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> {formatDate(event.date)}</div>
          <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {event.location}</div>
          <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /> {event.reservations_count}/{event.capacity} reserved</div>
        </div>
        <div className="mt-auto">
          {event.is_open_for_reservation ? (
            <button
              onClick={() => onReserve(event)}
              className="btn-ser-accent w-full"
              data-testid={`reserve-btn-${event.event_id}`}
            >
              Reserve your spot
            </button>
          ) : (
            <button disabled className="btn-ser-outline w-full" data-testid={`closed-btn-${event.event_id}`}>
              Reservations closed
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

function GridView({ events, onReserve }) {
  const featured = events.find(isSymposiumEvent);
  const rest = featured ? events.filter((e) => e.event_id !== featured.event_id) : events;
  return (
    <>
      {featured && <FeaturedRow event={featured} onReserve={onReserve} />}
      {rest.length > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {rest.map((e) => (
            <article key={e.event_id} className="ser-card flex flex-col" data-testid={`event-card-${e.event_id}`}>
              <div className="h-52 overflow-hidden relative">
                <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" />
                <span className="ser-tag absolute top-4 left-4">{e.category}</span>
                <div className="absolute top-4 right-4">
                  <StatusPill event={e} />
                </div>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-serif-display text-2xl mb-3" style={{ color: "var(--ser-primary-dark)" }}>
                  {e.title}
                </h3>
                <p className="text-sm mb-5 line-clamp-3" style={{ color: "var(--ser-text-muted)" }}>
                  {e.description}
                </p>
                <div className="space-y-2 text-xs mb-6" style={{ color: "var(--ser-text-muted)" }}>
                  <div className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" /> {formatDate(e.date)}</div>
                  <div className="flex items-center gap-2"><MapPin className="w-3.5 h-3.5" /> {e.location}</div>
                  <div className="flex items-center gap-2"><Users className="w-3.5 h-3.5" /> {e.reservations_count}/{e.capacity} reserved</div>
                </div>
                <div className="mt-auto">
                  {e.is_open_for_reservation ? (
                    <button onClick={() => onReserve(e)} className="btn-ser-accent w-full" data-testid={`reserve-btn-${e.event_id}`}>
                      Reserve your spot
                    </button>
                  ) : (
                    <button disabled className="btn-ser-outline w-full" data-testid={`closed-btn-${e.event_id}`}>
                      Reservations closed
                    </button>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </>
  );
}

function ListView({ events, onReserve }) {
  return (
    <div className="space-y-4">
      {events.map((e) => (
        <article
          key={e.event_id}
          className="ser-card p-5 flex flex-col md:flex-row gap-5"
          data-testid={`event-card-${e.event_id}`}
        >
          <div className="md:w-56 shrink-0 h-40 md:h-32 rounded-lg overflow-hidden relative">
            <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" />
            <span className="ser-tag absolute top-2 left-2 text-[10px]">{e.category}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h3 className="font-serif-display text-xl" style={{ color: "var(--ser-primary-dark)" }}>
                {e.title}
              </h3>
              <StatusPill event={e} />
            </div>
            <p className="text-sm mb-2 line-clamp-2" style={{ color: "var(--ser-text-muted)" }}>
              {e.description}
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ color: "var(--ser-text-muted)" }}>
              <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {formatDate(e.date)}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {e.location}</span>
              <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {e.reservations_count}/{e.capacity}</span>
            </div>
          </div>
          <div className="md:self-center shrink-0">
            {e.is_open_for_reservation ? (
              <button onClick={() => onReserve(e)} className="btn-ser-accent" data-testid={`reserve-btn-${e.event_id}`}>
                Reserve
              </button>
            ) : (
              <button disabled className="btn-ser-outline" data-testid={`closed-btn-${e.event_id}`}>
                Closed
              </button>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}
